import { format, parse, isValid } from 'date-fns';

/**
 * Date utility functions for ShiftGrid
 * Default format: DD-MM-YYYY
 */

export const DATE_FORMATS = {
  'DD-MM-YYYY': 'dd-MM-yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
} as const;

export type DateFormatType = keyof typeof DATE_FORMATS;

/**
 * Format a Date object to a string using the specified format
 * @param date - Date object to format
 * @param formatType - Format type (default: 'DD-MM-YYYY')
 * @returns Formatted date string
 */
export function formatDate(date: Date, formatType: DateFormatType = 'DD-MM-YYYY'): string {
  try {
    return format(date, DATE_FORMATS[formatType]);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Parse a date string to a Date object
 * @param dateString - Date string to parse
 * @param formatType - Format type used in the string (default: 'DD-MM-YYYY')
 * @returns Date object or null if invalid
 */
export function parseDate(dateString: string, formatType: DateFormatType = 'DD-MM-YYYY'): Date | null {
  try {
    const parsed = parse(dateString, DATE_FORMATS[formatType], new Date());
    return isValid(parsed) ? parsed : null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
}

/**
 * Format a Date object to Firestore-friendly format (YYYY-MM-DD)
 * Used as keys in hoursConfig and availability objects
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export function toFirestoreDateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Parse a Firestore date key to a Date object
 * @param dateKey - Date string in YYYY-MM-DD format
 * @returns Date object or null if invalid
 */
export function fromFirestoreDateKey(dateKey: string): Date | null {
  try {
    const parsed = parse(dateKey, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  } catch (error) {
    console.error('Error parsing Firestore date key:', error);
    return null;
  }
}

/**
 * Get current date formatted for display
 * @param formatType - Format type (default: 'DD-MM-YYYY')
 * @returns Formatted current date
 */
export function getCurrentDateFormatted(formatType: DateFormatType = 'DD-MM-YYYY'): string {
  return formatDate(new Date(), formatType);
}

/**
 * Get current date as Firestore key
 * @returns Current date in YYYY-MM-DD format
 */
export function getCurrentDateKey(): string {
  return toFirestoreDateKey(new Date());
}

/**
 * Get date range between two dates
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Array of Date objects
 */
export function getDateRange(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

/**
 * Get day of week from date
 * @param date - Date object
 * @returns Day of week (0 = Sunday, 1 = Monday, etc.)
 */
export function getDayOfWeek(date: Date): number {
  return date.getDay();
}

/**
 * Get day name from date
 * @param date - Date object
 * @returns Day name (e.g., 'monday', 'tuesday')
 */
export function getDayName(date: Date): string {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
}

/**
 * Check if a date string is valid in the given format
 * @param dateString - Date string to validate
 * @param formatType - Format type (default: 'DD-MM-YYYY')
 * @returns true if valid, false otherwise
 */
export function isValidDateString(dateString: string, formatType: DateFormatType = 'DD-MM-YYYY'): boolean {
  const parsed = parseDate(dateString, formatType);
  return parsed !== null;
}

/**
 * Get month name from date
 * @param date - Date object
 * @param short - Return short month name (e.g., 'Jan' instead of 'January')
 * @returns Month name
 */
export function getMonthName(date: Date, short: boolean = false): string {
  return format(date, short ? 'MMM' : 'MMMM');
}

/**
 * Get year-month string for Firestore queries
 * @param date - Date object
 * @returns Year-month string in YYYY-MM format
 */
export function getYearMonth(date: Date): string {
  return format(date, 'yyyy-MM');
}
